import * as Sentry from '@sentry/react';
import { isStaleAssetMessage } from './deployVersionSync';

/** Release ID for grouping errors and matching source maps (set in build: VITE_SENTRY_RELEASE). */
const SENTRY_RELEASE = (import.meta.env.VITE_SENTRY_RELEASE as string) || 'dugout-desk@dev';

/**
 * Error messages injected by browser extensions, embedded/in-app webviews, and
 * security browser bridges. None of these originate from our code, so we drop
 * them at the SDK level to keep the dashboard signal clean.
 */
const IGNORED_ERROR_PATTERNS: (string | RegExp)[] = [
  // Outlook SafeLink / Office crawler reflected back into the page.
  /Object Not Found Matching Id/i,
  // Android WebView teardown during navigation.
  'Java object is gone',
  /Error invoking postMessage/i,
  // Injected in-app/secure-browser bridge (e.g. Samsung Internet) not present in our bundle.
  /SCDynimacBridge/i,
  // Cross-origin sanitized errors (no stack) — often stale SW chunks or third-party scripts.
  /^Script error\.?$/i,
  // Chrome extension messaging into a tab that has already closed.
  /Invalid call to runtime\.sendMessage\(\)/,
  // Stale hashed assets after deploy — recovered via hard reload in deployVersionSync.
  /Importing a module script failed/i,
  /Failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /Unable to preload CSS/i,
  /ChunkLoadError/i,
];

/**
 * Drops events whose only cause is a transient connectivity failure. These are
 * already surfaced to the user as retryable and are not application bugs.
 */
function isTransientNetworkEvent(event: Sentry.ErrorEvent): boolean {
  if (event.tags?.networkFailure === 'true') {
    return true;
  }
  const values = event.exception?.values ?? [];
  return values.some(v => {
    const type = v.type ?? '';
    const value = v.value ?? '';
    return (
      /^(TypeError|TransientNetworkError)$/.test(type) &&
      /load failed|failed to fetch|network request failed|networkerror/i.test(value)
    );
  });
}

/**
 * iOS WebKit NotFoundError from removeChild/insertBefore. The DOM guard
 * swallows it; this drops any event that still reaches the SDK. Matches the
 * browser message only — API NotFoundError never hits this client filter.
 */
function isBrowserDomNotFoundEvent(event: Sentry.ErrorEvent): boolean {
  const values = event.exception?.values ?? [];
  return values.some(value => {
    const type = value.type ?? '';
    const message = value.value ?? '';
    return type === 'NotFoundError' && /the object can not be found here/i.test(message);
  });
}

/** Stale Vite chunks after deploy — recovered by installStaleAssetRecovery(). */
function isStaleAssetEvent(event: Sentry.ErrorEvent): boolean {
  const values = event.exception?.values ?? [];
  if (values.some(v => isStaleAssetMessage(v.value ?? '') || isStaleAssetMessage(v.type ?? ''))) {
    return true;
  }
  return isStaleAssetMessage(event.message ?? '');
}

/** True when `beforeSend` should drop the event. */
export function shouldDropSentryEvent(event: Sentry.ErrorEvent): boolean {
  return (
    isTransientNetworkEvent(event) || isStaleAssetEvent(event) || isBrowserDomNotFoundEvent(event)
  );
}

/** True when the message matches the SDK `ignoreErrors` list. */
export function isIgnoredClientErrorMessage(message: string): boolean {
  return IGNORED_ERROR_PATTERNS.some(pattern =>
    typeof pattern === 'string' ? message.includes(pattern) : pattern.test(message)
  );
}

const INVALID_ORIGIN_PATTERN = /invalid origin/i;

function reasonToDiagnostic(reason: unknown): Record<string, unknown> {
  const base: Record<string, unknown> = {
    reasonType: typeof reason,
    reasonValue: String(reason),
  };
  if (reason instanceof Error) {
    base.reasonMessage = reason.message;
    base.reasonStack = reason.stack;
    base.reasonName = reason.name;
  } else if (reason && typeof reason === 'object') {
    const obj = reason as { message?: unknown; stack?: unknown; name?: unknown };
    if (obj.message != null) base.reasonMessage = String(obj.message);
    if (obj.stack != null) base.reasonStack = String(obj.stack);
    if (obj.name != null) base.reasonName = String(obj.name);
  }
  if (typeof window !== 'undefined') {
    base.href = window.location.href;
    base.referrer = document.referrer;
    base.userAgent = navigator.userAgent;
  }
  return base;
}

/** Forensic capture for stack-less "invalid origin" rejections (often privacy browsers). */
function registerInvalidOriginDiagnostics() {
  if (typeof window === 'undefined') return;

  window.addEventListener('unhandledrejection', event => {
    try {
      const reason = event.reason;
      const haystack = [
        String(reason),
        reason instanceof Error ? reason.message : '',
        reason && typeof reason === 'object' && 'message' in reason
          ? String((reason as { message: unknown }).message)
          : '',
      ].join(' ');

      if (!INVALID_ORIGIN_PATTERN.test(haystack)) return;

      const extra = reasonToDiagnostic(reason);
      Sentry.addBreadcrumb({
        category: 'diagnostic',
        message: 'invalid_origin_unhandledrejection',
        data: extra,
        level: 'warning',
      });
      Sentry.captureMessage('invalid_origin_diagnostic', {
        level: 'warning',
        extra,
        tags: { diagnostic: 'invalid_origin' },
      });
    } catch {
      // never throw from observability
    }
  });
}

export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn || import.meta.env.DEV) return;

  Sentry.init({
    dsn,
    release: SENTRY_RELEASE,
    environment: import.meta.env.MODE,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
        // Skip iframes so the recorder does not walk cross-origin frames. Firefox
        // throws a security-wrapper error when rrweb reads a node it cannot access.
        block: ['iframe'],
        unblock: ['.sentry-unblock', '[data-sentry-unblock]'],
        unmask: ['.sentry-unmask', '[data-sentry-unmask]'],
      }),
    ],
    ignoreErrors: IGNORED_ERROR_PATTERNS,
    beforeSend(event) {
      if (shouldDropSentryEvent(event)) return null;
      return event;
    },
    tracesSampleRate: 0.05,
    replaysSessionSampleRate: 0.0,
    replaysOnErrorSampleRate: 1.0,
  });

  registerInvalidOriginDiagnostics();
}

/** Call when auth state changes so errors are tied to a user. */
export function setSentryUser(
  user: {
    id: string | number;
    email?: string | null;
    name?: string | null;
    role?: string | null;
    teamId?: string | null;
    teamName?: string | null;
    tournamentId?: string | null;
    organizationId?: string | null;
  } | null
) {
  if (!import.meta.env.VITE_SENTRY_DSN) return;
  if (!user) {
    Sentry.setUser(null);
    Sentry.setTag('role', '');
    Sentry.setTag('teamId', '');
    Sentry.setTag('teamName', '');
    Sentry.setTag('tournamentId', '');
    Sentry.setTag('organizationId', '');
    return;
  }
  Sentry.setUser({
    id: String(user.id),
    email: user.email ?? undefined,
    username: user.name ?? undefined,
  });
  Sentry.setTag('role', user.role ?? '');
  if (user.teamId) Sentry.setTag('teamId', user.teamId);
  if (user.teamName) Sentry.setTag('teamName', user.teamName);
  if (user.tournamentId) Sentry.setTag('tournamentId', user.tournamentId);
  if (user.organizationId) Sentry.setTag('organizationId', user.organizationId);
}

/** Set context tags for filtering in Sentry (e.g. page, tournamentId). */
export function setSentryTags(tags: Record<string, string | number | boolean | undefined>) {
  if (!import.meta.env.VITE_SENTRY_DSN) return;
  Object.entries(tags).forEach(([key, value]) => {
    if (value !== undefined && value !== '') Sentry.setTag(key, String(value));
  });
}
